import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, ArrowRight, X, Check, SkipForward, Mic, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import DictableTextarea from '@/components/DictableTextarea';

const OGGI = () => new Date().toISOString().slice(0, 10);
const turnoCorrente = () => {
  const h = new Date().getHours();
  return h < 14 ? 'mattino' : h < 21 ? 'pomeriggio' : 'notte';
};

export default function GiroDiario() {
  const navigate = useNavigate();
  const [ospiti, setOspiti] = useState([]);
  const [area, setArea] = useState('tutte');
  const [indice, setIndice] = useState(0);
  const [testo, setTesto] = useState('');
  const [fatti, setFatti] = useState(0);
  const [giaScritte, setGiaScritte] = useState({});
  const [turno, setTurno] = useState(turnoCorrente());
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    api.get('/ospiti', { params: { stato: 'presente' } }).then(r => setOspiti(r.data)).catch(() => {});
    api.get('/diario', { params: { data: OGGI() } }).then(r => {
      const mappa = {};
      r.data.forEach(n => { mappa[n.ospite_id] = (mappa[n.ospite_id] || 0) + 1; });
      setGiaScritte(mappa);
    }).catch(() => {});
  }, []);

  const coda = useMemo(() => {
    const lista = ospiti.filter(o => area === 'tutte' || o.area === area);
    return [...lista].sort((a, b) => (giaScritte[a.id] || 0) - (giaScritte[b.id] || 0)
      || String(a.stanza).localeCompare(String(b.stanza)));
  }, [ospiti, area, giaScritte]);

  const corrente = coda[indice];

  const vai = (delta) => { setIndice(i => Math.min(Math.max(i + delta, 0), coda.length - 1)); setTesto(''); };

  const salva = async () => {
    if (!testo.trim()) return toast.error('Detta o scrivi la nota prima di salvare');
    setSalvando(true);
    try {
      await api.post('/diario', {
        ospite_id: corrente.id, data: OGGI(), turno,
        contenuto: testo.trim(), tags: ['giro_diario'],
      });
      toast.success(`Nota salvata per ${corrente.cognome} ${corrente.nome}`);
      setGiaScritte(g => ({ ...g, [corrente.id]: (g[corrente.id] || 0) + 1 }));
      setFatti(f => f + 1);
      if (indice >= coda.length - 1) setIndice(coda.length); else vai(1);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore nel salvataggio'); }
    setSalvando(false);
  };

  if (!ospiti.length) return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand" /></div>;

  if (indice >= coda.length) {
    return (
      <div className="max-w-lg mx-auto py-20 text-center" data-testid="giro-diario-completato">
        <BookOpen className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-slate-900 font-[Cinzel]">Giro diario completato</h1>
        <p className="text-sm text-slate-500 mt-2">{fatti} note registrate in questo giro.</p>
        <Button className="mt-6 bg-brand hover:bg-brand-dark text-white" onClick={() => navigate('/diario')} data-testid="torna-diario-btn">
          Torna al diario clinico
        </Button>
      </div>
    );
  }

  const progresso = Math.round((indice / coda.length) * 100);

  return (
    <div className="fixed inset-0 z-50 bg-slate-50 overflow-y-auto" data-testid="giro-diario-page">
      <div className="sticky top-0 bg-white border-b border-slate-200 px-4 sm:px-8 py-3">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wider text-slate-400">Giro diario vocale · {indice + 1} di {coda.length}</p>
              <h1 className="text-lg font-bold text-slate-900 truncate">{corrente.cognome} {corrente.nome}</h1>
              <p className="text-[11px] text-slate-400">Camera {corrente.stanza || '-'}/{corrente.letto || '-'} · {corrente.area || 'N/D'}
                {giaScritte[corrente.id] ? ` · ${giaScritte[corrente.id]} note gia oggi` : ' · nessuna nota oggi'}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge className="bg-emerald-100 text-emerald-700 text-[10px]" data-testid="giro-diario-fatti">{fatti} salvate</Badge>
              <Button variant="ghost" size="sm" onClick={() => navigate('/diario')} data-testid="chiudi-giro-diario-btn"><X className="w-4 h-4" /></Button>
            </div>
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full mt-3 overflow-hidden">
            <div className="h-full bg-brand transition-all duration-500" style={{ width: `${progresso}%` }} />
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-6 space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={area} onValueChange={v => { setArea(v); setIndice(0); setTesto(''); }}>
            <SelectTrigger className="w-40 h-9 text-xs" data-testid="giro-diario-area"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tutte">Tutte le aree</SelectItem>
              <SelectItem value="verde">Area verde</SelectItem>
              <SelectItem value="gialla">Area gialla</SelectItem>
            </SelectContent>
          </Select>
          <Select value={turno} onValueChange={setTurno}>
            <SelectTrigger className="w-40 h-9 text-xs" data-testid="giro-diario-turno"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="mattino">Turno mattino</SelectItem>
              <SelectItem value="pomeriggio">Turno pomeriggio</SelectItem>
              <SelectItem value="notte">Turno notte</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-[11px] text-slate-400 flex items-center gap-1">
            <Mic className="w-3.5 h-3.5" />Premi il microfono nel riquadro e parla: il testo resta modificabile
          </span>
        </div>

        <Card>
          <CardContent className="p-4">
            <DictableTextarea rows={8} value={testo} onChange={e => setTesto(e.target.value)}
              placeholder="Osservazioni del turno: condizioni generali, alimentazione, idratazione, riposo, umore, eventi..."
              contesto={`Nota di diario clinico per ${corrente.cognome} ${corrente.nome}`}
              className="text-sm" data-testid="giro-diario-testo" />
          </CardContent>
        </Card>

        <div className="flex items-center justify-between gap-2 pb-10">
          <Button variant="outline" size="sm" disabled={indice === 0} onClick={() => vai(-1)} data-testid="giro-diario-precedente">
            <ArrowLeft className="w-4 h-4 mr-1" />Precedente
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => vai(1)} data-testid="giro-diario-salta">
              <SkipForward className="w-4 h-4 mr-1" />Salta
            </Button>
            <Button size="sm" onClick={salva} disabled={salvando} className="bg-brand hover:bg-brand-dark text-white" data-testid="giro-diario-salva">
              <Check className="w-4 h-4 mr-1" />{salvando ? 'Salvataggio...' : 'Salva e continua'}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
